import React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';

import Layout from '../components/layout';
import SEO from '../components/seo';
import EpisodesPerYear from '../components/stats/episodes-per-year';
import AvgDurationPerYear from '../components/stats/avg-duration-per-year';
import MostPopularEpisodes from '../components/stats/most-popular-episodes';
import GenderRatio from '../components/stats/gender-ratio';
import LongestEpisodes from '../components/stats/longest-episodes';
import ShortestEpisodes from '../components/stats/shortest-episodes';

import { secondsToHHMMSS } from '../utils/seconds-to-hhmmss';
import { durationToSeconds } from '../utils/duration-to-seconds';

export default function StatsPage() {
  const data = useStaticQuery(graphql`
    query {
      allEpisodesJson(sort: { fields: fields___durationSeconds, order: ASC }) {
        totalCount
        edges {
          node {
            id
            guest
            duration
            gender
            publishedAt(formatString: "MMM D, YYYY")
            fields {
              durationSeconds
            }
          }
        }
      }
    }
  `);

  const numEpisodes = data.allEpisodesJson.totalCount;
  const totalDurationSec = data.allEpisodesJson.edges
    .map((edge) => durationToSeconds(edge.node.duration))
    .reduce((a, b) => a + b, 0);
  const totalDuration = secondsToHHMMSS(totalDurationSec);
  const totalDurationDays = Math.round((totalDurationSec / 86400) * 10) / 10;
  const avgDuration = secondsToHHMMSS(totalDurationSec / numEpisodes);

  const cards = [
    {
      label: 'Episodes',
      data: `${numEpisodes}`,
    },
    {
      label: 'Total duration',
      data: `${totalDuration} / ~${totalDurationDays} days`,
    },
    {
      label: 'Average duration',
      data: `${avgDuration}`,
    },
  ];

  const MainStats = ({ cards }) => (
    <div className="sm:px-20 md:px-0 md:mt-5 md:mb-10 grid grid-cols-1 md:grid-cols-3 gap-5">
      {cards.map((card, idx) => (
        <div
          key={idx}
          className="border border-gray-700 hover:border-gray-600 rounded-lg shadow-2xl flex flex-col py-5 text-center align-center"
        >
          <div className="text-heading mb-3 text-2xl">{card.label}</div>
          <div className="text-2xl lg:text-3xl">{card.data}</div>
        </div>
      ))}
    </div>
  );

  return (
    <Layout>
      <SEO title="Stats" />
      <h2>Stats</h2>
      <MainStats cards={cards} />
      <div className="md:flex md:flex-wrap justify-start gap-x-5">
        <div className="mt-7 md:mt-0 md:flex md:flex-col w-full lg:max-w-lg md:gap-y-8">
          <MostPopularEpisodes />
        </div>
        <div className="mt-7 mb-7 lg:mt-0 md:max-w-sm">
          <GenderRatio />
        </div>
        <div className="mt-7 xl:mt-0 md:flex md:flex-col">
          <EpisodesPerYear />
          <AvgDurationPerYear />
        </div>
      </div>
      <div className="mt-7 md:flex flex-wrap gap-10">
        <LongestEpisodes />
        <ShortestEpisodes />
      </div>
      <div className="mt-7">
        {/* TODO: Get this data dynamically */}
        <h3 className="mb-3 text-2xl">Most frequent guests</h3>
        <p className="md:mb-0">
          Based on the total number of appearances,{' '}
          <span className="text-hl">Michael Malice</span> (
          <Link to="/episodes/253-michael-malice">#253</Link>,{' '}
          <Link to="/episodes/200-michael-malice">#200</Link>,{' '}
          <Link to="/episodes/178-michael-malice-yaron-brook">#178</Link>,{' '}
          <Link to="/episodes/150-michael-malice">#150</Link>,{' '}
          <Link to="/episodes/128-michael-malice">#128</Link>) is the most
          frequent guest on LFP with a total of{' '}
          <span className="text-hl">5 appearances</span>.
        </p>
      </div>
      <details className="mt-10 mb-3" open>
        <summary>
          <h3 className="inline">Trivia</h3>
        </summary>
        <ul className="mb-1 ml-1 border-l border-gray-500 pl-4 list-disc list-inside leading-loose">
          <li>
            The <Link to="/episodes/1-max-tegmark">first LFP episode</Link> was
            aired on Apr 19, 2018 with a conversation with Max Tegmark.
          </li>
          <li>
            The official name for the podcast was changed from "Artificial
            Intelligence Podcast" to "Lex Fridman Podcast" on{' '}
            <a href=" https://www.youtube.com/watch?v=U41dkEpj6NU">
              Aug 19, 2020
            </a>
            , exactly 2 years from the day the first episode aired.
          </li>
        </ul>
      </details>
      <p>
        For more detailed stats, visit{' '}
        <Link to="/episodes/sort/">/episodes/sort</Link>.
      </p>
    </Layout>
  );
}
